export const SELECTED_LANGUAGE_STORAGE_KEY = 'sesc.selectedLanguage';

// Canonical list of locale codes accepted by the i18n layer.
// Declared as a const tuple so consumers (e.g. dayjsLocaleMap) can derive a
// literal union type and catch missing-mapping regressions at compile time.
export const SUPPORTED_LANGUAGES = [
  'cs',
  'da',
  'de',
  'en',
  'en_US',
  'es',
  'fr',
  'fr_CA',
  'hu',
  'hr',
  'it',
  'nl_NL',
  'pl',
  'ro',
  'sk',
  'sl',
  'sr_Latn'
] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/**
 * UI-facing metadata for a supported language.
 *
 * - `name`: native display name used in the language picker list (e.g. "Čeština").
 * - `display`: compact label used elsewhere in the UI (e.g. user nav badge "EN", "FR (CA)").
 */
export interface LanguageMetadata {
  name: string;
  display: string;
}

/**
 * Per-locale UI metadata.
 *
 * Typed as Record<SupportedLanguage, LanguageMetadata> so adding a new code to
 * SUPPORTED_LANGUAGES surfaces a TS error here until the entry is supplied —
 * preventing the metadata table from drifting from the canonical locale list.
 */
export const LANGUAGE_METADATA: Record<SupportedLanguage, LanguageMetadata> = {
  cs: { name: 'Čeština', display: 'cs' },
  da: { name: 'Dansk', display: 'da' },
  de: { name: 'Deutsch', display: 'de' },
  en: { name: 'British English', display: 'en' },
  en_US: { name: 'English US', display: 'en (US)' },
  es: { name: 'Español', display: 'es' },
  fr: { name: 'Français', display: 'fr' },
  fr_CA: { name: 'Français canadien', display: 'fr (CA)' },
  hu: { name: 'Magyar', display: 'hu' },
  hr: { name: 'Hrvatski', display: 'hr' },
  it: { name: 'Italiano', display: 'it' },
  nl_NL: { name: 'Nederlands', display: 'nl' },
  pl: { name: 'Polski', display: 'pl' },
  ro: { name: 'Română', display: 'ro' },
  sk: { name: 'Slovensky', display: 'sk' },
  sl: { name: 'Slovenščina', display: 'sl' },
  sr_Latn: { name: 'Srpski', display: 'sr (Latn)' }
};

/**
 * A supported language code paired with its UI metadata.
 * Used by language picker components that need both the code and the labels.
 */
export interface LanguageOption extends LanguageMetadata {
  code: SupportedLanguage;
}

/**
 * Iterable list of supported languages with UI metadata, in canonical
 * SUPPORTED_LANGUAGES order. Derived at module load — kept in sync by
 * construction since both sources are the same tuple.
 */
export const LANGUAGES: readonly LanguageOption[] = SUPPORTED_LANGUAGES.map(
  (code) => ({ code, ...LANGUAGE_METADATA[code] })
);
